import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import pollService from "../../api/pollService";
import logoDark from "../../assets/images/logo-dark.svg";
import { useTheme } from "../../context/ThemeContext";
import "./PollVote.scss";

function useLogoSrc() {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === "dark" ? logoDark : process.env.PUBLIC_URL + "/logo.svg";
}

// Public multi-select poll vote form.
//
// Reached via the "Take this poll →" link in a campaign email (multi-select
// polls only — single-select polls use the one-tap links that land on
// PollVoteResult instead). Unlike that page, this one actually runs: it
// fetches the poll by token, renders real checkboxes, and posts the
// selection — a browser can do this even though the email itself can't.

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

function StatusCard({ badge, icon, title, subtitle }) {
  const logoSrc = useLogoSrc();
  return (
    <div className="poll-vote-page">
      <div className="poll-vote-card">
        <div className="poll-vote-card__header">
          <img src={logoSrc} alt="MapMyPay" className="poll-vote-logo" />
          <div className={`poll-vote-badge ${badge}`}>{icon}</div>
        </div>
        <div className="poll-vote-card__body">
          <h1 className="poll-vote-title">{title}</h1>
          <p className="poll-vote-subtitle">{subtitle}</p>
          <a className="poll-vote-return" href={process.env.PUBLIC_URL + "/"}>
            Return to MapMyPay
          </a>
        </div>
      </div>
    </div>
  );
}

const PollVoteForm = () => {
  const logoSrc = useLogoSrc();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [loadStatus, setLoadStatus] = useState(null); // 'ok' | 'expired' | 'already-voted' | 'invalid'
  const [poll, setPoll] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!token) {
      setLoadStatus("invalid");
      setLoading(false);
      return;
    }

    let cancelled = false;
    pollService
      .getPollByToken(token)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data?.data || {};
        setLoadStatus(body.status || "invalid");
        if (body.status === "ok") setPoll(body.poll);
      })
      .catch((err) => {
        // Logged (not shown to the recipient) so a real cause — network
        // failure, CORS, unexpected 5xx — is diagnosable instead of always
        // reading as a generic "invalid link" in the UI.
        console.error("Poll fetch failed:", err?.response?.status, err?.response?.data || err?.message);
        if (!cancelled) setLoadStatus("invalid");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  function toggleOption(optionId) {
    setFormError("");
    // Single-select polls (also linked from this page's Vote button, not
    // just multi-select ones) only ever hold one selection at a time.
    if (poll && !poll.is_multi_select) {
      setSelectedIds([optionId]);
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (selectedIds.length === 0) {
      setFormError("Select at least one option before voting.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await pollService.submitPollVote(token, selectedIds);
      const status = res?.data?.data?.status;
      if (status === "expired") {
        setLoadStatus("expired");
      } else if (status === "already-voted") {
        setLoadStatus("already-voted");
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "We couldn't record your vote. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <StatusCard
        badge=""
        icon={<ClockIcon />}
        title="Loading poll…"
        subtitle="Just a moment."
      />
    );
  }

  if (submitted) {
    return (
      <StatusCard
        badge="is-success"
        icon={<CheckIcon />}
        title="Thanks for voting!"
        subtitle="Your response has been recorded."
      />
    );
  }

  if (loadStatus === "expired") {
    return (
      <StatusCard
        badge="is-warning"
        icon={<ClockIcon />}
        title="This poll has expired"
        subtitle="Voting has closed for this poll, so your response couldn't be recorded."
      />
    );
  }

  if (loadStatus === "already-voted") {
    return (
      <StatusCard
        badge="is-warning"
        icon={<ClockIcon />}
        title="You've already voted"
        subtitle="We already have a response from you on this poll — thanks for weighing in!"
      />
    );
  }

  if (loadStatus === "error") {
    return (
      <StatusCard
        badge="is-error"
        icon={<AlertIcon />}
        title="Something went wrong"
        subtitle="We couldn't load this poll just now. Please try the link again in a moment."
      />
    );
  }

  if (loadStatus !== "ok" || !poll) {
    return (
      <StatusCard
        badge="is-error"
        icon={<AlertIcon />}
        title="This voting link isn't valid"
        subtitle="The link may be old or malformed. Please use the latest email if you'd like to vote."
      />
    );
  }

  const options = (poll.options || []).slice().sort(
    (a, b) => (a.display_order || 0) - (b.display_order || 0)
  );

  return (
    <div className="poll-vote-page">
      <div className="poll-vote-card">
        <div className="poll-vote-card__header">
          <img src={logoSrc} alt="MapMyPay" className="poll-vote-logo" />
        </div>
        <div className="poll-vote-card__body">
          <h1 className="poll-vote-title">{poll.question}</h1>
          {poll.description && <p className="poll-vote-description">{poll.description}</p>}

          <form onSubmit={handleSubmit}>
            <div className="poll-vote-options">
              {options.map((opt) => {
                const checked = selectedIds.includes(opt.id);
                return (
                  <label key={opt.id} className={`poll-vote-option${checked ? " is-checked" : ""}`}>
                    <input
                      type={poll.is_multi_select ? "checkbox" : "radio"}
                      name="poll-option"
                      checked={checked}
                      onChange={() => toggleOption(opt.id)}
                      disabled={submitting}
                    />
                    <span>{opt.option_text}</span>
                  </label>
                );
              })}
            </div>

            {formError && <p className="poll-vote-error">{formError}</p>}

            <button type="submit" className="poll-vote-submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit vote"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PollVoteForm;
